import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { ProductModelUtils } from '@app/products/shared/product.model';
import { EventModel } from '@app/events/shared/event.model';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { ProductItemFormComponent } from '@app/events/view/product-item-form.component';
import { Gift, LucideAngularModule, Star } from 'lucide-angular';

export interface OfferSelectionPayload {
  selectedExistingIds: number[];
  newItems: EventProductItemModel[];
}

@Component({
  selector: 'app-offer-products-selection',
  imports: [CommonModule, ProductItemFormComponent, NgClass, LucideAngularModule],
  templateUrl: './offer-products-selection.component.html',
  styleUrls: ['./offer-products-selection.component.css'],
})
export class OfferProductsSelectionComponent implements OnInit, OnChanges {
  protected readonly ProductModelUtils = ProductModelUtils;
  protected readonly GiftIcon = Gift;
  protected readonly StarIcon = Star;
  @Input() event!: EventModel;
  @Input() eligibleItems: EventProductItemModel[] = [];
  @Input() currentOfferItemIds: number[] = [];
  @Input() blockedItemIds: number[] = [];
  @Input() processing = false;
  @Input() errorMessage: string | null = null;
  @Output() offerApplied = new EventEmitter<OfferSelectionPayload>();
  @Output() cancelled = new EventEmitter<void>();
  @ViewChild(ProductItemFormComponent) offerProductForm?: ProductItemFormComponent;

  selectedItems: EventProductItemModel[] = [];
  stagedNewItems: EventProductItemModel[] = [];
  maxOfferAmount = 150;
  private hasUserInteraction = false;
  private initialOfferItemIds: Set<number> = new Set();

  // TVG-278: the £150 is a discount pool, not a hard cap on how many offer products
  // may be flagged — the backend applies it in id order and bills any excess at full
  // price (see the "anything above £150 is charged at full price" note in the view).
  // This getter now only drives that informational note; it must NOT block adding or
  // ticking further offer products, otherwise the credit reads as permanently
  // "used up" and cannot be reallocated after a covered product is removed and re-added.
  get isOverOfferCredit(): boolean {
    return this.selectedTotal > this.maxOfferAmount;
  }

  get appliedExistingItems(): EventProductItemModel[] {
    const currentIds = new Set((this.currentOfferItemIds ?? []).map(id => Number(id)));
    return (this.eligibleItems ?? []).filter(item => currentIds.has(item.id));
  }

  ngOnInit(): void {
    this.selectedItems = this.selectedItems.filter(item => this.isEligibleItem(item));
    this.syncSelectedProducts(true);
    this.initialOfferItemIds = new Set(this.currentOfferItemIds ?? []);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eligibleItems'] || changes['currentOfferItemIds']) {
      this.selectedItems = this.selectedItems.filter(item => this.isEligibleItem(item));
      this.syncSelectedProducts(false);
    }
  }

  get selectedTotal(): number {
    const existingTotal = this.selectedItems.reduce((sum, item) => sum + this.getItemPrice(item), 0);
    const stagedTotal = this.stagedNewItems.reduce((sum, item) => sum + this.getItemPrice(item), 0);
    return existingTotal + stagedTotal;
  }

  isSelected(item: EventProductItemModel): boolean {
    return this.selectedItems.some(selected => selected.id === item.id);
  }

  toggleProduct(item: EventProductItemModel): void {
    if (this.isCheckboxDisabled(item)) {
      return;
    }

    this.hasUserInteraction = true;

    const index = this.selectedItems.findIndex(selected => selected.id === item.id);
    if (index >= 0) {
      // Unselect - remove from array
      this.selectedItems = [
        ...this.selectedItems.slice(0, index),
        ...this.selectedItems.slice(index + 1)
      ];
    } else {
      // Select - add to array (create new array to trigger change detection)
      this.selectedItems = [...this.selectedItems, item];
    }
  }

  applyOffer(): void {
    if (this.processing) return;
    const payload: OfferSelectionPayload = {
      selectedExistingIds: this.selectedItems.map(item => item.id),
      newItems: [...this.stagedNewItems],
    };
    this.offerApplied.emit(payload);
  }

  cancel(): void {
    this.hasUserInteraction = false;
    this.stagedNewItems = [];
    this.syncSelectedProducts(true);
    this.cancelled.emit();
  }

  stageNewOfferItem(item: EventProductItemModel): void {
    // TVG-278: no £150 face-value gate here — staff may add further offer products
    // and the backend spreads the £150 in id order, billing any excess at full price.
    // Ensure we mark staged items as offer items for clarity
    item.is_offer_item = true;
    this.stagedNewItems = [...this.stagedNewItems, item];
  }

  removeStagedItem(itemId: number): void {
    this.stagedNewItems = this.stagedNewItems.filter(item => item.id !== itemId);
  }

  protected isBlocked(item: EventProductItemModel): boolean {
    if (!item || !this.blockedItemIds?.length) {
      return false;
    }

    return this.blockedItemIds.includes(item.id);
  }

  isCheckboxDisabled(item: EventProductItemModel): boolean {
    if (this.processing) {
      return true;
    }

    // Items required to keep the £500 qualifying spend cannot be turned into offer
    // items (that would drop the event below the threshold), but everything else is
    // freely selectable — TVG-278 removed the £150 face-value block so the credit can
    // always be reallocated across offer products.
    if (this.isBlocked(item) && !this.isSelected(item)) {
      return true;
    }

    return false;
  }

  private isEligibleItem(item: EventProductItemModel): boolean {
    if (!item) {
      return false;
    }

    const eligibleIds = new Set(
      (this.appliedExistingItems ?? []).map(candidate => candidate.id)
    );

    return eligibleIds.has(item.id);
  }

  private syncSelectedProducts(force: boolean): void {
    if (this.hasUserInteraction && !force) {
      return;
    }

    const selectedIds = new Set(
      (this.currentOfferItemIds ?? [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id))
    );

    const eligibleItems = (this.eligibleItems ?? []);
    this.selectedItems = eligibleItems.filter(item => selectedIds.has(item.id));
  }

  private getItemPrice(item?: EventProductItemModel): number {
    if (!item || item.calculated_price === undefined || item.calculated_price === null) {
      return 0;
    }
    const price = Number(item.calculated_price);
    return Number.isFinite(price) ? price : 0;
  }

  get hasChanges(): boolean {
    const stagedCount = this.stagedNewItems.length;
    const selectedIds = new Set(this.selectedItems.map(i => i.id));
    if (stagedCount > 0) return true;
    if (selectedIds.size !== this.initialOfferItemIds.size) return true;
    for (const id of selectedIds) {
      if (!this.initialOfferItemIds.has(id)) {
        return true;
      }
    }
    return false;
  }
  constructor() {}
}
